import { useWalletClient } from 'wagmi';
import { Surface } from '@dapp/multi-lib/sdaoui/Surface';
import { Title1, Body2, Body3, Title4, Body1 } from 'components/Typography';
import React, { useEffect, useState } from 'react';
import { VaultInfoItem } from 'structure/pv2/vault/components/VaultInfoItem';
import { Icon } from '@dapp/multi-lib/sdaoui/Icon';
import { getExplorerLink, getNetworkByChainId } from '@dapp/multi-lib/network';
import Link from 'next/link';
import useIsMobile from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';
import { formatAddress } from '@dapp/multi-lib/lib/format-balance';
import { ButtonIcon } from '@dapp/multi-lib/sdaoui/ButtonIcon';
import { twMerge } from 'tailwind-merge';
import { Collapse } from '@dapp/multi-lib/sdaoui/Collapse';
import { DynaVault } from '../../../../api/DynaVaultsAPI';
import { formatDateMMDDYYYY } from 'utils/formateDate';
import { getNetworkIconUrlByChaiId } from 'utils/url';
import Image from 'next/image';
import { getVaultAPYGroup, getVaultPerformance } from '../utils/vault';
import { formatPercent } from '@dapp/multi-lib/lib/formatters';
import { VaultType } from '../configs/vaultTypes';
import { useTranslation } from 'react-i18next';

interface VaultDescriptionProps {
  vault: DynaVault;
}

export const VaultDescription = (props: VaultDescriptionProps) => {
  const { vault } = props;
  const isMobile = useIsMobile();
  const { t } = useTranslation('common');
  const [showDetails, setShowDetails] = useState(!isMobile);
  const { data: walletClient } = useWalletClient();
  const isPerformancesORapy =
    vault.type === VaultType.INDEX
      ? getVaultPerformance(vault)
      : getVaultAPYGroup(vault);

  const addTokenToMetamask = () => {
    try {
      walletClient?.watchAsset({
        type: 'ERC20',
        options: {
          address: vault.address,
          symbol: vault.symbol,
          decimals: vault.decimals,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setShowDetails(!isMobile);
  }, [isMobile]);

  return (
    <Surface rounded='large'>
      <div className='flex flex-col gap-6'>
        <div className='flex flex-row justify-between'>
          <Title1 className='font-bold text-[#D9D9D9]'>{vault.name}</Title1>
          <ButtonIcon
            className={twMerge(
              'self-start lg:hidden',
              'transition-all duration-300 ease-in-out',
              showDetails ? '-rotate-180' : 'rotate-0'
            )}
            onClick={() => setShowDetails((state) => !state)}
          >
            <Icon icon='arrow_down' color='#F0F0F0' width={16} height={16} />
          </ButtonIcon>
        </div>
        <Body2 className='text-[#A6A6A6]'>{vault.description}</Body2>
        <div className='flex flex-col items-start gap-4 md:flex-row md:gap-8'>
          <Body2 className='text-xs text-white'>
            {vault.type === 'INDEX'
              ? t('PERFORMANCE')
              : t('ANNUAL_PERCENTAGE_YIELD')}{' '}
            :
          </Body2>
          {isPerformancesORapy.map((performance, index) => {
            return (
              <div key={index} className='flex flex-col justify-start gap-2'>
                <Body2 className='text-xs text-[#BFBFBF]'>
                  {performance.title}
                </Body2>
                <Body1 className='text-base text-white'>
                  {performance.value
                    ? `${
                        parseFloat(performance?.value?.toString() || '0') > 1000
                          ? '+1000'
                          : formatPercent(performance.value)
                      }%`
                    : '--'}
                </Body1>
              </div>
            );
          })}
        </div>

        <Collapse
          className={showDetails ? 'block' : 'hidden'}
          visible={showDetails}
          type='vertical'
        >
          <div className='flex flex-row flex-wrap justify-between gap-4'>
            <VaultInfoItem
              icon={
                <Image
                  className='w-full'
                  src={getNetworkIconUrlByChaiId(vault.chainId)}
                  alt='network'
                  height='24'
                  width='24'
                />
              }
              label={
                <div className='flex flex-row items-center gap-1'>
                  <Body3 className='font-normal text-[#BFBFBF]'>Network:</Body3>
                  <Body2 className='font-normal text-white'>
                    {getNetworkByChainId(vault.chainId)?.name}
                  </Body2>
                </div>
              }
              value={
                <div className='flex flex-row items-center gap-1'>
                  <Body3 className='font-normal text-[#BFBFBF]'>
                    Contract:
                  </Body3>
                  <Body2 className='font-normal text-white'>
                    {isMobile ? formatAddress(vault.address) : vault.address}
                  </Body2>
                  <Icon
                    onClick={addTokenToMetamask}
                    icon='metamask'
                    color={'#d9d9d9'}
                    className='cursor-pointer'
                    width={16}
                    height={16}
                  />
                  <Link
                    target='_blank'
                    href={getExplorerLink(vault.chainId, vault.address)}
                  >
                    <Icon
                      icon='open_in_new'
                      color={'#d9d9d9'}
                      width={16}
                      height={16}
                    />
                  </Link>
                </div>
              }
            />

            <div className='flex flex-row gap-10'>
              <VaultInfoItem
                icon='calendar'
                label='Created at'
                value={
                  <Title4 className='font-bold text-white'>
                    {formatDateMMDDYYYY(new Date(vault.createdAt || '0'))}
                  </Title4>
                }
              />

              {vault.factsheets.length !== 0 && (
                <VaultInfoItem
                  icon='pdf'
                  label='Fact Sheet'
                  value={
                    <div className='flex flex-row items-center gap-2 lg:gap-3'>
                      <Title4 className='text-[0.75rem] leading-[1.125rem] text-white lg:text-[1rem] lg:leading-[1.125rem]'>
                        PDF
                      </Title4>
                      <div className='h-[16px] w-[16px] rounded-full border-2 border-white bg-black p-0.5'>
                        <Icon
                          icon='downlaod'
                          width='100%'
                          height='100%'
                          color={'#B4BCD0'}
                        />
                      </div>
                    </div>
                  }
                />
              )}
            </div>
          </div>
        </Collapse>
      </div>
    </Surface>
  );
};
